import { useCallback, useEffect, useRef, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import {
  CardTypes,
  IPaymentDetails,
  IPaymentPayload,
  RequestPaymentFn,
  paymentErrorType,
} from 'features/payment/types';

import { ICashAppResult } from 'components/payments/integrations/firstdata/types';
import {
  getQueryParams,
  loadScript,
  removeAllQueryParams,
} from 'components/payments/integrations/firstdata/utils';
import { getNonce } from 'remote/api/first-data';
import { selectors, useAppSelector } from 'state/global-state';
import { getConfigValue } from 'utils/environment';
import { isProduction } from 'utils/environment';
import { ISOs } from 'utils/form/constants';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

declare global {
  interface Window {
    CashApp?: unknown;
  }
}

type AfterPayButton = HTMLElement & {
  renderCashAppPayButton: (options: any) => void;
  initializeForCashAppPay: (options: any) => void;
  initializeCashAppPayListeners: (options: any) => void;
  restartCashAppPay: () => void;
};

const elementId = 'afterpay-button';

const SCRIPTS = [
  {
    id: elementId,
    url: isProduction
      ? 'https://static.afterpay.com/afterpay-button.js'
      : 'https://static-us.afterpay.com/javascript/button/afterpay-button.js',
  },
];

async function embedScripts(): Promise<void> {
  for (const script of SCRIPTS) {
    await loadScript(script.id, script.url);
  }
}

const createAfterPayButton = (cashAppMerchantId: string) => {
  const existingButton = document.querySelector(elementId);
  if (existingButton) {
    return existingButton;
  }

  // Create the afterpay-button element
  const afterPayButton = document.createElement(elementId);
  afterPayButton.setAttribute('merchantPublicKey', cashAppMerchantId);
  afterPayButton.setAttribute('isCashAppPay', 'true');

  // Append the button directly to the body
  document.body.appendChild(afterPayButton);
  return afterPayButton;
};

const getAfterPayButton = () => {
  return document.querySelector(elementId) as AfterPayButton;
};

const disposeAfterPayButton = () => {
  const afterPayButton = document.querySelector(elementId);
  if (afterPayButton) {
    document.body.removeChild(afterPayButton);
  }
  removeAllQueryParams();
};

const bindAttributesToButton = (button: AfterPayButton, amount: number, currency = 'USD') => {
  button.setAttribute('amount', (amount / 100).toFixed(2));
  button.setAttribute('currency', currency);
};

const getRedirectConfirmUrl = (tip: number, pricedOrderId?: string) => {
  if (window.location.pathname.includes(routes.cartCheckout) && pricedOrderId) {
    return `${window.location.origin}${window.location.pathname}?tip=${tip}&rbiOrderId=${pricedOrderId}`;
  }
  return `${window.location.origin}${window.location.pathname}?tip=${tip}`;
};

export const useRequestCashAppPayment: RequestPaymentFn = ({ cashapp: cashAppPaymentInput }) => {
  const { total = 0, tip = 0 } = cashAppPaymentInput ?? {};
  const { loading, getEncryptionDetails } = usePaymentContext();
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const [data, setData] = useState<IPaymentDetails>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<paymentErrorType>();
  const redirectConfirmUrl = getRedirectConfirmUrl(tip, pricedOrderId);
  const [isRedirected, setIsRedirected] = useState(false);
  const initErrorCount = useRef(0);

  const { cashAppMerchantId, baseUrl } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });

  const init = useCallback(async () => {
    try {
      if (total < 100) {
        return;
      }

      await embedScripts();
      createAfterPayButton(cashAppMerchantId);
      const afterPayButton = getAfterPayButton();

      if (!afterPayButton) {
        return;
      }

      // wait for afterpay sdk to be loaded
      await new Promise(resolve => setTimeout(resolve, 300));

      const cashAppPayButtonOptions = {
        button: false,
        manage: false,
        eventListeners: {
          CUSTOMER_REQUEST_DECLINED: () => {
            logger.warn({
              message:
                '[useRequestCashAppPayment][init]: Error occurred while requesting Cash App payment.',
              event: 'CUSTOMER_REQUEST_DECLINED',
            });
            setError(paymentErrorType.cashAppDeclined);
            afterPayButton.restartCashAppPay();
            disposeAfterPayButton();
            setIsRedirected(false);
            setIsLoading(false);
          },
          CUSTOMER_REQUEST_FAILED: () => {
            logger.error({
              message:
                '[useRequestCashAppPayment][init]: Error occurred while requesting Cash App payment.',
              event: 'CUSTOMER_REQUEST_FAILED',
            });
            setError(paymentErrorType.cashAppGeneric);
            afterPayButton.restartCashAppPay();
            disposeAfterPayButton();
            setIsRedirected(false);
            setIsLoading(false);
          },
          CUSTOMER_DISMISSED: () => {
            logger.warn({
              message:
                '[useRequestCashAppPayment][init]: Error occurred while requesting Cash App payment.',
              event: 'CUSTOMER_DISMISSED',
            });
            setError(paymentErrorType.cashAppDeclined);
            afterPayButton.restartCashAppPay();
            disposeAfterPayButton();
            setIsRedirected(false);
            setIsLoading(false);
          },
        },
        onBegin: ({ begin }: { begin: () => void }) => {
          bindAttributesToButton(afterPayButton, total);

          afterPayButton.initializeForCashAppPay({
            redirectConfirmUrl,
            onError: (event: any) => {
              logger.warn({
                message:
                  '[useRequestCashAppPayment][init]: Error occurred while initializing Cash App payment.',
                event,
                isRedirected,
              });

              if (afterPayButton.restartCashAppPay) {
                afterPayButton.restartCashAppPay();
                disposeAfterPayButton();
                initErrorCount.current++;
                if (initErrorCount.current < 3) {
                  init();
                }
              } else {
                setIsLoading(false);
                setError(paymentErrorType.cashAppGeneric);
                logger.error({
                  message:
                    '[useRequestCashAppPayment][init]: Error occurred while initializing Cash App payment after 3 attempts.',
                  event,
                });
              }
            },
            onComplete: async (cardInfo: { data: ICashAppResult }) => {
              setIsLoading(true);
              const encryptionDetails = await getEncryptionDetails();
              const { data } = cardInfo;

              const payload: IPaymentPayload = {
                billingAddress: {
                  unitNumber: null,
                  locality: null,
                  postalCode: '',
                  region: null,
                  streetAddress: null,
                  country: ISOs.US,
                },
                cardNumber: data.virtualCard.cardNumber,
                cardType: CardTypes.VISA,
                expiryDate: {
                  month: data.virtualCard.expiryMonth,
                  year: data.virtualCard.expiryYear,
                },
                nameOnCard: '',
                securityCode: data.virtualCard.cvc,
              };

              const nonceResponse = await getNonce(
                payload,
                encryptionDetails.fdPublicKey,
                encryptionDetails.fdApiKey,
                encryptionDetails.fdAccessToken,
                encryptionDetails.fdCustomerId || null,
                baseUrl,
                true,
                encryptionDetails.algorithm || undefined
              );
              const nonce = await nonceResponse.json();
              setData({
                cashappDetails: {
                  ...data,
                  fdAccessToken: encryptionDetails.fdAccessToken,
                  fdNonce: nonce.token.tokenId,
                },
              });
              setIsLoading(false);
            },
          });

          if (!isRedirected) {
            setIsLoading(false);
            begin();
          }
        },
      };

      if (isRedirected) {
        afterPayButton.initializeCashAppPayListeners({
          countryCode: 'US',
          cashAppPayListenerOptions: cashAppPayButtonOptions,
        });
      }

      afterPayButton.renderCashAppPayButton({
        countryCode: 'US',
        cashAppPayButtonOptions,
      });
    } catch (error) {
      logger.error({
        message:
          '[useRequestCashAppPayment][init]: Error occurred while initializing Cash App payment.',
        error,
      });
      disposeAfterPayButton();
      setIsLoading(false);
    }
  }, [baseUrl, cashAppMerchantId, getEncryptionDetails, isRedirected, redirectConfirmUrl, total]);

  useEffect(() => {
    const queryParams = getQueryParams();
    const isRedirectedInParams = (queryParams as any)?.isRedirected === 'true';
    if (isRedirectedInParams && !isRedirected) {
      setIsRedirected(true);
      setIsLoading(true);
    }
    if (isRedirectedInParams && !loading) {
      init();
    }
  }, [init, isRedirected, loading]);

  const requestPayment = useCallback(async () => {
    const afterPayButton = getAfterPayButton();
    initErrorCount.current = 0;
    setError(undefined);
    if (afterPayButton) {
      afterPayButton.restartCashAppPay();
      disposeAfterPayButton();
    }
    init();
  }, [init]);

  return { data, isLoading, error, requestPayment };
};
